// Signed-in persistence in Firestore: users/{uid}, preferences/main,
// savedStories/{storyId}. Functions take a Firestore instance so the same code
// is exercised by the emulator tests.
import {
  Timestamp,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  writeBatch,
  type Firestore,
} from "firebase/firestore/lite";
import { ROLES } from "../../shared/roles.ts";
import {
  AUDIENCES,
  CLOUDS,
  DEFAULT_PREFERENCES,
  LENSES,
  LIMITS,
  TOPICS,
  type Preferences,
  type SavedStory,
} from "../../shared/model.ts";
import { changedItems, mergeBaskets, normalize, type Basket } from "./basket.ts";

const savedCol = (db: Firestore, uid: string) => collection(db, "users", uid, "savedStories");
const prefsDoc = (db: Firestore, uid: string) => doc(db, "users", uid, "preferences", "main");

export async function ensureUserDoc(db: Firestore, uid: string): Promise<void> {
  const ref = doc(db, "users", uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) await setDoc(ref, { createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
}

export async function loadAccountBasket(db: Firestore, uid: string): Promise<Basket> {
  const snap = await getDocs(query(savedCol(db, uid), orderBy("order"), limit(LIMITS.maxSavedStories)));
  return normalize(
    snap.docs.map((d) => {
      const v = d.data();
      return {
        storyId: d.id,
        savedAt: v.savedAt instanceof Timestamp ? v.savedAt.toMillis() : 0,
        order: typeof v.order === "number" ? v.order : 0,
        isRead: v.isRead === true,
        readAt: v.readAt instanceof Timestamp ? v.readAt.toMillis() : null,
        readingProgress: typeof v.readingProgress === "number" ? v.readingProgress : null,
      } satisfies SavedStory;
    }),
  );
}

export async function loadAccountPrefs(db: Firestore, uid: string): Promise<Preferences | null> {
  const snap = await getDoc(prefsDoc(db, uid));
  if (!snap.exists()) return null;
  const p = snap.data();
  return {
    audience: AUDIENCES.includes(p.audience) ? p.audience : DEFAULT_PREFERENCES.audience,
    lens: LENSES.includes(p.lens) ? p.lens : DEFAULT_PREFERENCES.lens,
    role: (ROLES as readonly string[]).includes(p.role) ? p.role : "all",
    followedTopics: Array.isArray(p.followedTopics) ? p.followedTopics.filter((t: string) => (TOPICS as readonly string[]).includes(t)) : [],
    preferredClouds: Array.isArray(p.preferredClouds) ? p.preferredClouds.filter((c: string) => (CLOUDS as readonly string[]).includes(c)) : [],
  };
}

function toDoc(item: SavedStory) {
  const out: Record<string, unknown> = {
    savedAt: Timestamp.fromMillis(item.savedAt),
    order: item.order,
    isRead: item.isRead,
    updatedAt: serverTimestamp(),
  };
  if (item.readAt !== null) out.readAt = Timestamp.fromMillis(item.readAt);
  if (item.readingProgress !== null) out.readingProgress = item.readingProgress;
  return out;
}

/** Writes only what changed between two basket states, in one batch. */
export async function persistBasketDiff(db: Firestore, uid: string, before: Basket, after: Basket): Promise<number> {
  const { upserts, deletes } = changedItems(before, after);
  if (!upserts.length && !deletes.length) return 0;
  const batch = writeBatch(db);
  for (const item of upserts) batch.set(doc(savedCol(db, uid), item.storyId), toDoc(item));
  for (const id of deletes) batch.delete(doc(savedCol(db, uid), id));
  await batch.commit();
  return upserts.length + deletes.length;
}

export async function savePreferences(
  db: Firestore,
  uid: string,
  prefs: Preferences,
  opts: { markVisit?: boolean } = {},
): Promise<void> {
  const data: Record<string, unknown> = {
    audience: prefs.audience,
    lens: prefs.lens,
    role: prefs.role,
    followedTopics: prefs.followedTopics,
    preferredClouds: prefs.preferredClouds,
    updatedAt: serverTimestamp(),
  };
  if (opts.markVisit) data.lastVisitedAt = serverTimestamp();
  await setDoc(prefsDoc(db, uid), data, { merge: true });
}

export interface SignInResult {
  basket: Basket;
  prefs: Preferences;
  mergedCount: number;
}

/**
 * Called once after sign-in. Reads the account basket once, merges guest
 * saves without duplicates, and writes only the resulting differences.
 */
export async function mergeGuestIntoAccount(
  db: Firestore,
  uid: string,
  guestBasket: Basket,
  guestPrefs: Preferences,
): Promise<SignInResult> {
  await ensureUserDoc(db, uid);
  const [account, accountPrefs] = await Promise.all([loadAccountBasket(db, uid), loadAccountPrefs(db, uid)]);
  const merged = mergeBaskets(account, guestBasket);
  await persistBasketDiff(db, uid, account, merged);
  const prefs = accountPrefs ?? guestPrefs;
  await savePreferences(db, uid, prefs, { markVisit: true });
  const accountIds = new Set(account.map((a) => a.storyId));
  return { basket: merged, prefs, mergedCount: guestBasket.filter((g) => !accountIds.has(g.storyId)).length };
}
